import React, { useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";

// Remote engines (loaded from Supabase Storage)
window.__CHART_ENGINE_URL__ =
  "https://capenshjrcnuesgyowck.supabase.co/storage/v1/object/public/logic/chartEngine.js";
window.__PRICE_ENGINE_URL__ =
  "https://capenshjrcnuesgyowck.supabase.co/storage/v1/object/public/logic/priceEngine.js";

function getEnv(...keys) {
  try {
    if (typeof process !== "undefined" && process.env) {
      for (const k of keys) if (process.env[k]) return process.env[k];
    }
  } catch {}
  try {
    const viteEnv = new Function(
      "try { return import.meta && import.meta.env } catch(e){ return undefined }"
    )();
    if (viteEnv) for (const k of keys) if (viteEnv[k]) return viteEnv[k];
  } catch {}
  try {
    if (typeof window !== "undefined" && window.__ENV__) {
      for (const k of keys) if (window.__ENV__[k]) return window.__ENV__[k];
    }
  } catch {}
  return "";
}

// Debug keys for canvas only
const LOCAL_DEBUG = {
  url: "https://capenshjrcnuesgyowck.supabase.co",
  key: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNhcGVuc2hqcmNudWVzZ3lvd2NrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM2NTQ4OTAsImV4cCI6MjA3OTIzMDg5MH0.HnOgbGKw6AYmq8ZxvnTjKw929_gvCRUznbQltjhRnAE",
};

const isCanvas = true;

const SUPABASE_URL =
  getEnv("REACT_APP_SUPABASE_URL", "VITE_SUPABASE_URL") ||
  (isCanvas ? LOCAL_DEBUG.url : "");
const SUPABASE_ANON_KEY =
  getEnv("REACT_APP_SUPABASE_ANON_KEY", "VITE_SUPABASE_ANON_KEY") ||
  (isCanvas ? LOCAL_DEBUG.key : "");

const supabase =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
    : null;

async function loadPriceEngine() {
  const code = await fetch(window.__PRICE_ENGINE_URL__).then((r) => r.text());
  const module = {};
  new Function("module", code)(module);
  return module.exports;
}

async function loadChartEngine() {
  const url =
    window.__CHART_ENGINE_URL__ ||
    "https://capenshjrcnuesgyowck.supabase.co/storage/v1/object/public/logic/chartEngine.js";
  const code = await fetch(url).then((r) => r.text());
  const module = {};
  new Function("module", code)(module);
  return module.exports;
}

// ---------------- Live price hook (NO game logic here) ----------------
function useLiveSolanaPrice() {
  const [engine, setEngine] = useState(null);
  const [state, setState] = useState({ price: null, history: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pe = await loadPriceEngine();
        if (!cancelled) setEngine(() => pe);
      } catch (e) {
        console.error("Failed to load PriceEngine", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!engine) return;
    let stop = false;

    async function tick() {
      try {
        const next = await engine.tick(state);
        if (!stop && next) setState(next);
      } catch (e) {
        console.error("PriceEngine tick error", e);
      }
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [engine, state]);

  return { price: state.price, history: state.history };
}

function LiveChart({ price, history }) {
  const [engine, setEngine] = useState(null);
  const [flash, setFlash] = useState(null);
  const [points, setPoints] = useState("");
  const [last, setLast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ce = await loadChartEngine();
        if (!cancelled) setEngine(() => ce);
      } catch (e) {
        console.error("Failed to load ChartEngine", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!engine) return;
    if (!history || history.length < 2) return;
    try {
      const f = engine.getFlash(last, price);
      setFlash(f);
      const p = engine.buildChartPoints(history);
      setPoints(p);
      setLast(price);
    } catch (e) {
      console.error("ChartEngine error", e);
    }
  }, [engine, price, history, last]);

  if (!points) {
    return <div className="text-slate-400 text-sm">Loading chart…</div>;
  }

  return (
    <div className="w-full bg-transparent border-none">
      <div className="w-full h-80 relative">
        <svg viewBox="0 0 400 100" className="w-full h-full">
          <polyline
            fill="none"
            stroke="currentColor"
            className="text-emerald-400"
            strokeWidth={2}
            points={points}
          />
        </svg>
        <div
          className={`absolute inset-0 flex items-center justify-center text-5xl font-bold tabular-nums transition ${
            flash === "up"
              ? "text-emerald-400"
              : flash === "down"
              ? "text-rose-400"
              : "text-slate-100"
          }`}
        >
          {price ? `$${price.toFixed(2)}` : "--"}
        </div>
        <div className="absolute bottom-2 inset-x-0 text-center text-xs text-slate-400">
          Solana live price (updates every second)
        </div>
      </div>
    </div>
  );
}

// ---------------- Supabase helpers ----------------
async function getOrCreateUserRow(emailVal, authUid) {
  if (!supabase) return null;
  const safeEmail = (emailVal || "").trim().toLowerCase();
  const safeUid = authUid || null;
  if (!safeEmail) return null;

  try {
    const { data: existing, error: selErr } = await supabase
      .from("users")
      .select("id_uuid, email, sol_address")
      .eq("email", safeEmail)
      .limit(1);
    if (selErr) throw selErr;

    let row = existing?.[0];
    if (!row) {
      const insertPayload = safeUid
        ? { id_uuid: safeUid, email: safeEmail }
        : { email: safeEmail };

      const { data: inserted, error: insErr } = await supabase
        .from("users")
        .insert(insertPayload)
        .select("id_uuid, email, sol_address")
        .limit(1);
      if (insErr) throw insErr;
      row = inserted?.[0];
    }
    return row || null;
  } catch (err) {
    console.error("users table lookup/create failed", err);
    return null;
  }
}

async function ensureDepositAddress(authUid, emailVal) {
  try {
    if (!authUid || !emailVal) return null;
    const res = await fetch("/api/deposit-address", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: authUid, email: emailVal }),
    });
    const json = await res.json().catch(() => ({}));
    return json.address || null;
  } catch (e) {
    console.warn("ensureDepositAddress failed", e);
    return null;
  }
}

// ---------------- AccountBar ----------------
function AccountBar({ account, setAccount }) {
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupAddress, setSignupAddress] = useState(null);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;

    async function restore() {
      try {
        const { data, error: getUserErr } = await supabase.auth.getUser();
        if (getUserErr) return;

        const user = data?.user;
        if (!user || !user.email) return;

        const emailVal = user.email.toLowerCase();
        const row = await getOrCreateUserRow(emailVal, user.id);
        if (!row?.id_uuid) return;

        await ensureDepositAddress(user.id, emailVal);

        const { data: urow } = await supabase
          .from("users")
          .select("sol_address")
          .eq("id_uuid", user.id)
          .maybeSingle();

        if (!cancelled) {
          setAccount({
            authId: user.id,
            email: emailVal,
            userId: user.id,
            solAddress: urow?.sol_address || row?.sol_address || null,
          });
        }
      } catch (e) {
        console.error("restore error", e);
      }
    }

    restore();
    return () => {
      cancelled = true;
    };
  }, [setAccount]);

  async function handleAuth() {
    if (!supabase) {
      setError("Supabase is not configured. Check env vars.");
      return;
    }
    setError("");
    setLoading(true);
    setSignupSuccess(false);
    setSignupAddress(null);

    try {
      const safeEmail = email.trim().toLowerCase();
      if (!safeEmail || !password) throw new Error("Enter email and password.");

      if (mode === "signup") {
        const { data: suData, error: suErr } = await supabase.auth.signUp({
          email: safeEmail,
          password,
          options: { emailRedirectTo: null },
        });
        if (suErr) throw suErr;

        const newUser = suData?.user;
        const uid = newUser?.id || null;
        if (uid) {
          await getOrCreateUserRow(safeEmail, uid);
          const addr = await ensureDepositAddress(uid, safeEmail);

          const { data: urow } = await supabase
            .from("users")
            .select("sol_address")
            .eq("id_uuid", uid)
            .maybeSingle();

          setAccount({
            authId: uid,
            email: safeEmail,
            userId: uid,
            solAddress: addr || urow?.sol_address || null,
          });

          setSignupSuccess(true);
          setSignupAddress(addr || urow?.sol_address || null);
        }

        setShowLogin(false);
        return;
      }

      const { data, error: siErr } = await supabase.auth.signInWithPassword({
        email: safeEmail,
        password,
      });
      if (siErr) throw siErr;

      const user = data?.user;
      if (!user?.email) throw new Error("No user returned.");
      const emailVal = user.email.toLowerCase();

      const row = await getOrCreateUserRow(emailVal, user.id);
      await ensureDepositAddress(user.id, emailVal);

      const { data: urow } = await supabase
        .from("users")
        .select("sol_address")
        .eq("id_uuid", user.id)
        .maybeSingle();

      setAccount({
        authId: user.id,
        email: emailVal,
        userId: user.id,
        solAddress: urow?.sol_address || row?.sol_address || null,
      });

      setShowLogin(false);
    } catch (e) {
      setError(e.message || "Auth error");
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    if (!supabase) return;
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error("logout error", e);
    }
    setAccount(null);
  }

  return (
    <div className="mb-4">
      {!supabase && (
        <div className="mb-2 text-xs text-rose-400">
          Supabase env vars missing. Set REACT_APP_SUPABASE_URL and
          REACT_APP_SUPABASE_ANON_KEY in Vercel.
        </div>
      )}

      <div className="flex justify-between items-center">
  {/* Removed top logout button */}
  {!account && (
    <button
      onClick={() => setShowLogin(true)}
      className="px-4 py-2 rounded-xl bg-emerald-400 text-slate-900 text-sm font-semibold shadow-sm hover:shadow-md transition"
    >
      Play with Real SOL
    </button>
  )}
</div>

      {showLogin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 p-6 rounded-2xl w-full max-w-sm space-y-4 border border-slate-700">
            <div className="text-slate-200 text-sm font-semibold">
              {mode === "login" ? "Login" : "Create Account"}
            </div>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            />

            {error && <div className="text-xs text-rose-400">{error}</div>}

            {signupSuccess && (
              <div className="p-3 rounded-xl bg-slate-800 border border-slate-700 text-xs text-slate-200 space-y-2">

                <div className="font-mono break-all text-[0.7rem]">
                  {signupAddress || "Generating address…"}
                </div>
                <button
                  onClick={() =>
                    signupAddress && navigator.clipboard?.writeText(signupAddress)
                  }
                  className="px-2 py-1 rounded-md bg-slate-900 text-slate-200 text-[0.7rem] border border-slate-600"
                >
                  Copy address
                </button>
                <div className="text-slate-400 text-[0.7rem] leading-relaxed">
                  Send SOL here anytime to fund your account. Log in to start
                  playing with real SOL.
                </div>
              </div>
            )}

            <button
              onClick={handleAuth}
              disabled={loading}
              className="w-full py-2 rounded-lg bg-emerald-400 text-slate-900 text-sm font-semibold disabled:opacity-50"
            >
              {loading
                ? "Please wait…"
                : mode === "login"
                ? "Login"
                : "Create Account"}
            </button>

            <button
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="w-full py-2 rounded-lg bg-slate-700 text-slate-200 text-sm"
            >
              {mode === "login" ? "Create new account" : "Back to login"}
            </button>

            <button
              onClick={() => setShowLogin(false)}
              className="w-full py-2 rounded-lg bg-slate-700 text-slate-200 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Ticket Game ----------------
function TicketGame({ price, account, balance, setBalance }) {
  const demoMode = !account;

  const [inputAmount, setInputAmount] = useState("");
  const [error, setError] = useState("");

  const [ticketAmount, setTicketAmount] = useState(null);
  const [wins, setWins] = useState(0);
  const [roundActive, setRoundActive] = useState(false);
  const [timer, setTimer] = useState(0);
  const [startPrice, setStartPrice] = useState(null);
  const [roundResult, setRoundResult] = useState("");
  const [ticketStatus, setTicketStatus] = useState("none");
  const [activeTicketId, setActiveTicketId] = useState(null);

  const [demoSessionId, setDemoSessionId] = useState(null);
  const [demoRoundNumber, setDemoRoundNumber] = useState(0);

  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [solAddress, setSolAddress] = useState("");
  const [redeemMessage, setRedeemMessage] = useState("");

  const timerRef = useRef(null);

  async function handleBuyTicket() {
    const num = Number(inputAmount);
    if (Number.isNaN(num)) return setError("Please enter a valid number.");
    if (num < 0.1) return setError("Minimum ticket price is 0.1 SOL.");
    if (!price) return setError("Wait for live price before starting.");
    if (!account?.userId) return setError("Please login to your account first.");
    if (balance === null) return setError("Loading your balance, please wait.");
    if (balance < num) return setError("Insufficient balance. Deposit more SOL.");

    try {
      setError("");
      const newBalance = balance - num;
      const { error: upErr } = await supabase
        .from("balances")
        .update({ sol_balance: newBalance })
        .eq("user_id", account.userId);
      if (upErr) throw upErr;
      setBalance(newBalance);

      const { data: existingActive } = await supabase
        .from("tickets")
        .select("id")
        .eq("user_id", account.userId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1);
      if (existingActive?.length) {
        setError("You already have an active ticket. Finish it before buying a new one.");
        return;
      }

      await supabase
        .from("tickets")
        .update({ status: "expired" })
        .eq("user_id", account.userId)
        .eq("status", "active");

      const { data: insertedTicket, error: insTicketErr } = await supabase
        .from("tickets")
        .insert({
          user_id: account.userId,
          amount: num,
          wins: 0,
          status: "active",
          created_at: new Date().toISOString(),
        })
        .select("id")
        .limit(1);
      if (insTicketErr) throw insTicketErr;
      const newId = insertedTicket?.[0]?.id || null;
      setActiveTicketId(newId);

      setTicketAmount(num);
      setWins(0);
      setTicketStatus("active");
      setRoundResult("");
      setShowRedeemForm(false);
      setRedeemMessage("");
    } catch (e) {
      console.error("buy error", e);
      setError("Could not complete purchase.");
    }
  }

  async function fetchClosingPrice() {
    try {
      const res = await fetch(
        "https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT"
      );
      const data = await res.json();
      return Number(data.price) || null;
    } catch {
      return null;
    }
  }

  function placeBet(direction) {
    if (!price || ticketStatus !== "active" || roundActive) return;

    if (demoMode && !demoSessionId) {
      const sid = `demo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setDemoSessionId(sid);
      setDemoRoundNumber(0);
    }

    const nextRoundNumber = demoMode ? demoRoundNumber + 1 : null;
    if (demoMode) setDemoRoundNumber(nextRoundNumber);

    setRoundActive(true);
    setTimer(10);

    if (timerRef.current) window.clearInterval(timerRef.current);
    const id = window.setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    timerRef.current = id;

    const start = price;
    setStartPrice(start);
    setRoundResult("");

    window.setTimeout(async () => {
      const closing = await fetchClosingPrice();
      const end = closing != null ? closing : start;

      const correct =
        (direction === "up" && end > start) ||
        (direction === "down" && end < start) ||
        (direction === "same" && end === start);

      if (demoMode && supabase && demoSessionId) {
        try {
          await supabase.from("demo_games").insert({
            session_id: demoSessionId,
            round_number: nextRoundNumber || 1,
            prediction: direction,
            correct: !!correct,
            start_price: start,
            end_price: end,
            created_at: new Date().toISOString(),
          });
        } catch (e) {
          console.warn("demo_games insert failed", e);
        }
      }

      if (!correct) {
        const actual = end > start ? "UP" : end < start ? "DOWN" : "SAME";

        setTicketStatus("expired");
        setActiveTicketId(null);

        if (!demoMode && activeTicketId && account?.userId) {
          supabase
            .from("tickets")
            .update({ status: "expired" })
            .eq("id", activeTicketId)
            .eq("user_id", account.userId);

          try {
            await fetch("/api/forward-on-loss", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId: account.userId,
                ticketId: activeTicketId,
                amount: ticketAmount,
                reason: "ticket_loss",
              }),
            });
          } catch (e) {
            console.warn("collect-loss failed", e);
          }
        }

        setRoundResult(
          `Wrong prediction. You chose: ${direction.toUpperCase()}. Actual: ${actual}. Start: $${start.toFixed(
            2
          )} → Close: $${end.toFixed(2)}.`
        );
        setRoundActive(false);
        return;
      }

      setWins((prev) => {
        const nw = prev + 1;
        if (!demoMode && activeTicketId && account?.userId) {
          supabase
            .from("tickets")
            .update({
              wins: nw,
              status: nw >= 10 ? "won" : "active",
            })
            .eq("id", activeTicketId)
            .eq("user_id", account.userId);
        }
        if (nw >= 10) {
          setTicketStatus("won");
          setRoundResult(
            `Amazing! Start: $${start.toFixed(2)} → Close: $${end.toFixed(
              2
            )}. You got 10 predictions correct. Your ticket is now worth 10x.`
          );
        } else {
          setRoundResult(
            `Correct! Start: $${start.toFixed(2)} → Close: $${end.toFixed(
              2
            )}. You now have ${nw} / 10 correct predictions.`
          );
        }
        return nw;
      });

      setRoundActive(false);
    }, 10000);
  }

  function submitRedeem() {
    if (demoMode) {
      setRedeemMessage("Redeeming is not available in Demo Mode.");
      return;
    }

    if (ticketStatus !== "won" || wins !== 10) {
      setRedeemMessage("Redeem available only after 10/10 correct predictions.");
      return;
    }

    if (!solAddress || solAddress.length < 20) {
      setRedeemMessage("Invalid SOL address.");
      return;
    }

    fetch("/api/send-sol", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: account?.userId,
        userEmail: account?.email,
        address: solAddress,
        ticketId: activeTicketId,
        wins: wins,
        ticketAmount: ticketAmount,
        amount: (ticketAmount || 0) * 10,
      }),
    }).catch(() => {});

    setRedeemMessage(
      "Your SOL address has been submitted. You will receive payout shortly."
    );
  }

  function resetTicket() {
    setTicketAmount(null);
    setWins(0);
    setRoundActive(false);
    setStartPrice(null);
    setRoundResult("");
    setTicketStatus("none");
    setError("");
    setShowRedeemForm(false);
    setRedeemMessage("");
  }

  // Reset demo ticket if user logs in
  useEffect(() => {
    if (account && demoSessionId) {
      // clear demo
      setDemoSessionId(null);
      setDemoRoundNumber(0);
      setTicketAmount(null);
      setWins(0);
      setRoundActive(false);
      setStartPrice(null);
      setRoundResult("");
      setTicketStatus("none");
    }
  }, [account]);

  const hasTicket = ["active", "won", "expired"].includes(ticketStatus);
  const ticketValue = ticketAmount
    ? ticketAmount * (ticketStatus === "won" ? 10 : 1)
    : 0;

  return (
    <div className="bg-transparent border-none space-y-4">
      <h2 className="text-sm font-semibold">Solana Price Bet 10x Ticket Game</h2>

      {!account && ticketStatus === "none" && (
        <div className="space-y-3 text-xs text-slate-300">
          <div className="text-slate-400">Demo Mode (No SOL required)</div>
          <button
            onClick={() => {
              const sid = `demo_${Date.now()}_${Math.random()
                .toString(36)
                .slice(2, 8)}`;
              setDemoSessionId(sid);
              setDemoRoundNumber(0);

              setTicketAmount(0.1);
              setWins(0);
              setTicketStatus("active");
              setRoundResult("");
            }}
            className="px-4 py-2 rounded-xl bg-indigo-400 text-slate-900 text-sm font-semibold w-full"
          >
            Get New Demo Ticket (0.1 SOL value)
          </button>

          <div className="text-slate-500 space-y-2 text-xs leading-relaxed p-3 rounded-xl bg-slate-900/40 border border-slate-800">
            <p className="text-slate-400 font-semibold">
              Demo predictions do not use real funds.
            </p>
            <p>
              Solana Price Bet game is about betting on future price with a
              10‑second interval.
            </p>

            <div className="pt-1">
              <p className="font-semibold text-slate-300 mb-1">How to play?</p>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li>Buy a ticket.</li>
                <li>Guess Solana’s future price 10 times in a row.</li>
                <li>Redeem your ticket with 10× value of your purchase price.</li>
                <li>If you fail any prediction, you lose the ticket.</li>
                <li>If you get all 10 correct, you 10× your initial ticket value.</li>
              </ul>
            </div>

            <p className="text-slate-400">
              Example: Buying a 0.1 SOL ticket and guessing correctly 10 times
              allows you to redeem 1 SOL. If you guess incorrect you lose 0.1 SOL.
            </p>
          </div>
        </div>
      )}

      {account && !hasTicket && (
        <div className="space-y-3">
          <div className="text-xs text-slate-400">
            Buy a ticket to start predicting SOL moves.
          </div>
          <div className="flex gap-2 items-center">
<input
  type="number"
  min="0.1"
  step="0.1"
  value={inputAmount}
  onChange={(e) => setInputAmount(e.target.value)}
  placeholder="Ticket price in SOL (minimum 0.1 SOL)"
  className="flex-1 px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-sm placeholder:text-xs placeholder:text-slate-400"
/>

            <button
              onClick={handleBuyTicket}
              className="px-4 py-2 rounded-xl bg-emerald-400 text-slate-900 text-sm font-semibold"
            >
              Buy
            </button>
          </div>
          {error && <div className="text-xs text-rose-400">{error}</div>}
        </div>
      )}

      {hasTicket && (
        <div className="space-y-2 text-xs text-slate-300">
          <div>
            Ticket amount:{" "}
            <span className="font-semibold">
              {ticketAmount?.toFixed(3)} SOL
            </span>
          </div>
          <div>
            Current ticket value:{" "}
            <span className="font-semibold">
              {ticketValue.toFixed(3)} SOL
            </span>
            {ticketStatus === "won" && " (10× boosted)"}
          </div>
          <div>
            Correct predictions:{" "}
            <span className="font-semibold">{wins} / 10</span>
          </div>
          <div>
            Status:{" "}
            <span
              className={`font-semibold ${
                ticketStatus === "expired" ? "text-rose-400" : "text-emerald-400"
              }`}
            >
              {ticketStatus}
            </span>
          </div>
        </div>
      )}

      {ticketStatus === "active" && (
        <div className="space-y-3">
          <div className="text-xs text-slate-400">
            Predict where SOL will be in{" "}
            <span className="font-semibold text-slate-200">10 seconds</span>.
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => placeBet("up")}
              disabled={roundActive || !price}
              className="w-full flex-1 py-3 rounded-2xl bg-emerald-400 text-slate-900 font-semibold disabled:opacity-50"
            >
              Price UP
            </button>
            <button
              onClick={() => placeBet("same")}
              disabled={roundActive || !price}
              className="w-full flex-1 py-3 rounded-2xl bg-slate-400 text-slate-900 font-semibold disabled:opacity-50"
            >
              Price SAME
            </button>
            <button
              onClick={() => placeBet("down")}
              disabled={roundActive || !price}
              className="w-full flex-1 py-3 rounded-2xl bg-rose-500 text-white font-semibold disabled:opacity-50"
            >
              Price DOWN
            </button>
          </div>
          {roundActive && startPrice !== null && (
            <div className="text-center text-xs text-slate-300">
              Round running… Start price: <strong>${startPrice.toFixed(2)}</strong>{" "}
              — waiting {timer}s.
            </div>
          )}
        </div>
      )}

      {roundResult && (
        <div className="text-center text-sm text-slate-200 mt-2">
          {roundResult}
        </div>
      )}

      {ticketStatus === "won" && (
        <div className="mt-4 text-center space-y-3">
          {!showRedeemForm ? (
            <>
              <div className="text-xs text-slate-400">
                Congratulations! Your ticket is worth 10×.
              </div>
              <button
                onClick={() => {
                  setShowRedeemForm(true);
                  setRedeemMessage("");
                }}
                className="px-4 py-2 rounded-xl bg-emerald-400 text-slate-900 text-sm font-semibold w-full"
              >
                Redeem Your Ticket
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Enter your SOL address"
                value={solAddress}
                onChange={(e) => setSolAddress(e.target.value)}
                className="w-full rounded-xl bg-slate-900 border border-slate-700 px-3 py-2 text-sm"
              />
              <button
                onClick={submitRedeem}
                className="px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-semibold w-full"
              >
                Submit Address
              </button>
              {redeemMessage && (
                <div className="text-xs text-slate-400">{redeemMessage}</div>
              )}
            </div>
          )}
        </div>
      )}

      {ticketStatus === "expired" && (
        <div className="mt-4 text-center space-y-2">
          <div className="text-sm font-semibold text-rose-400">
            Your 10x ticket has expired.
          </div>
          <button
            onClick={resetTicket}
            className="px-4 py-2 rounded-xl bg-slate-800 text-slate-100 text-sm font-semibold"
          >
            Get New Ticket
          </button>
        </div>
      )}
    </div>
  );
}

function WalletHistory({ account, compact = false }) {
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (!supabase || !account?.userId) {
      setTickets([]);
      return;
    }
    supabase
      .from("tickets")
      .select("amount,wins,status,created_at")
      .eq("user_id", account.userId)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error("ticket history load error", error);
        setTickets(data || []);
      });
  }, [account]);

  return (
    <div
      className={`bg-slate-900/50 border border-slate-800 p-4 rounded-2xl text-xs space-y-2 ${
        compact ? "max-h-72 overflow-auto" : ""
      }`}
    >
      <div className="font-semibold text-slate-200 mb-1">
        Your Ticket History
      </div>
      {tickets.length === 0 && (
        <div className="text-slate-500">No tickets yet.</div>
      )}
      {tickets.map((t, i) => (
        <div
          key={`${t.created_at}-${i}`}
          className="p-2 rounded-lg bg-slate-800/60 border border-slate-700"
        >
          <div className="text-slate-200">
            Amount: {Number(t.amount).toFixed(3)} SOL
          </div>
          <div className="text-slate-400">
            Wins: {t.wins}/10 · {t.status}
          </div>
          <div className="text-slate-500">
            {new Date(t.created_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- App ----------------
export default function App() {
  const [account, setAccount] = useState(null);
  const [showTicketPopup, setShowTicketPopup] = useState(false);
  const live = useLiveSolanaPrice();

  const [appBalance, setAppBalance] = useState(null);
  const [appBalanceLoading, setAppBalanceLoading] = useState(false);
  const [appBalanceError, setAppBalanceError] = useState("");

  // Load balance when account changes
  useEffect(() => {
    if (!supabase || !account?.userId) {
      setAppBalance(null);
      setAppBalanceError("");
      return;
    }

    let cancelled = false;
    (async () => {
      setAppBalanceLoading(true);
      setAppBalanceError("");
      try {
        const { data, error } = await supabase
          .from("balances")
          .select("sol_balance")
          .eq("user_id", account.userId)
          .limit(1);

        if (error) throw error;
        let row = data?.[0];
        if (!row) {
          const { data: inserted, error: insErr } = await supabase
            .from("balances")
            .insert({ user_id: account.userId, sol_balance: 0 })
            .select("sol_balance")
            .limit(1);
          if (insErr) throw insErr;
          row = inserted?.[0];
        }
        if (!cancelled) setAppBalance(Number(row?.sol_balance || 0));
      } catch {
        if (!cancelled) setAppBalanceError("Could not load balance.");
      } finally {
        if (!cancelled) setAppBalanceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [account]);

  // Auto-refresh balance every 15s (Edge Function updates every minute)
  useEffect(() => {
    if (!supabase || !account?.userId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await supabase
          .from("balances")
          .select("sol_balance")
          .eq("user_id", account.userId)
          .maybeSingle();
        if (data?.sol_balance != null) {
          setAppBalance(Number(data.sol_balance));
        }
      } catch (e) {
        console.error("Auto-refresh balance error", e);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [account]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 p-6 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        <AccountBar account={account} setAccount={setAccount} />

        {account && (
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setShowTicketPopup(true)}
              className="px-3 py-1 rounded-lg bg-emerald-400 text-slate-900 text-xs font-semibold"
            >
              Ticket History
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                setAccount(null);
              }}
              className="px-3 py-1 rounded-lg bg-slate-800 text-slate-200 text-xs border border-slate-600"
            >
              Logout
            </button>
          </div>
        )}

        {account && (
          <div className="text-[0.7rem] text-slate-400 pl-0">
            <div className="mt-1 text-[0.65rem] text-slate-500">
              Deposit SOL to charge your account:
            </div>
            <div className="flex items-center gap-2 mt-1 text-[0.65rem] text-slate-500">
  <span className="px-2 py-1 rounded-md bg-slate-800 text-slate-200 text-[0.65rem] border border-slate-700">{account.solAddress || "Loading deposit address…"}</span>
</div>
<div className="flex gap-2 items-center mt-2">
  <button
    onClick={() =>
      navigator.clipboard?.writeText(account.solAddress || "")
    }
    className="px-2 py-1 rounded-md bg-slate-800 text-slate-200 text-[0.65rem] border border-slate-700"
  >
    Copy address
  </button>

  {account?.email && (
    <span className="px-2 py-1 rounded-md bg-slate-800 text-slate-200 text-[0.65rem] border border-slate-700">
      {account.email}
    </span>
  )}
</div>

            <div className="mt-1 text-[0.65rem] text-slate-500">
  After sending, your balance will auto-update within ~1 minute.
</div>
  <div className="mt-1 text-[0.65rem] text-slate-500">
    Current balance:{" "}
    <span className="mt-1 text-[0.65rem] text-slate-500">{appBalance ?? 0} SOL</span>
  </div>
                       </div>
          )}

         


        {showTicketPopup && account && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 p-6 rounded-2xl w-full max-w-lg border border-slate-700 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-slate-200 text-sm font-semibold">
                  Your Ticket History
                </div>
                <button
                  onClick={() => setShowTicketPopup(false)}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  Close
                </button>
              </div>
              <WalletHistory account={account} compact />
              <button
                onClick={() => setShowTicketPopup(false)}
                className="w-full py-2 rounded-lg bg-slate-700 text-slate-200 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}

        <LiveChart price={live.price} history={live.history} />

        <div className="grid md:grid-cols-[2fr,1fr] gap-4 items-start">
          <TicketGame
            price={live.price}
            account={account}
            balance={appBalance}
            setBalance={setAppBalance}
          />
        </div>
      </div>
    </div>
  );
}

if (isCanvas && typeof window !== "undefined") {
  window.__solbet_devtests__ = () => {
    try {
      console.log(
        "✅ DevTests loaded: Live price hook + TicketGame are separated correctly."
      );
    } catch (e) {
      console.warn("DevTests failed", e);
    }
  };
  window.__solbet_devtests__();
}
